import React, { useState, useEffect } from "react";
import { cn } from "utils";
import Input from "components/Input";
import Select from "components/Select";
import { Button } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createEmployeeBenefit, updateEmployeeBenefit, getEmployeeBenefit, getCompanyBenefits } from "services/benefits";
import { getEmployees } from "services/employees";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createNotification } from "services/timeOff";

const notifyEmployee = (employeeId, title, message) => {
    const cid = localStorage.getItem('companyId');
    if (!employeeId || !cid) return;
    createNotification({ recipientId: employeeId, recipientType: 'employee', type: 'benefit_enrollment', title, message, companyId: cid }).catch(() => {});
};

const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);

const BENEFIT_TYPES = [
    { value: "Medical", label: "Medical" },
    { value: "Dental", label: "Dental" },
    { value: "Vision", label: "Vision" },
    { value: "401k", label: "401(k)" },
    { value: "HSA", label: "HSA" },
    { value: "FSA", label: "FSA" },
    { value: "LifeInsurance", label: "Life Insurance" },
    { value: "Disability", label: "Disability" },
    { value: "Other", label: "Other" },
];

const BENEFIT_PERIODS = [
    { value: "Weekly", label: "Weekly" },
    { value: "BiWeekly", label: "Bi-Weekly" },
    { value: "SemiMonthly", label: "Semi-Monthly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Annually", label: "Annually" },
];

const toDateInput = (val) => { if (!val) return ''; return val.substring(0, 10); };

const initialForm = {
    employeeId: "", companyBenefitId: "", type: "",
    effectiveStart: "", effectiveEnd: "", period: "",
    employeeContributionAmount: "", employeeContributionPercent: "",
    companyContributionAmount: "", companyContributionPercent: "",
    hsaContributionLimit: "",
};

const ContribToggle = ({ mode, onToggle }) => (
    <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #dee2e6', marginBottom: 6 }}>
        <button type="button"
            style={{ padding: '4px 14px', background: mode === 'amount' ? '#1B75F0' : '#fff', color: mode === 'amount' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer' }}
            onClick={() => onToggle('amount')}>$ Amount</button>
        <button type="button"
            style={{ padding: '4px 14px', background: mode === 'percent' ? '#1B75F0' : '#fff', color: mode === 'percent' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', borderLeft: '1px solid #dee2e6', cursor: 'pointer' }}
            onClick={() => onToggle('percent')}>% Percent</button>
    </div>
);

const AddEditEmployeeBenefit = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const benefitId = searchParams.get('id');
    const isEditMode = !!benefitId;
    const returnTo = searchParams.get('returnTo') || '/benefits';

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [employeeMode, setEmployeeMode] = useState('amount');
    const [companyMode, setCompanyMode] = useState('amount');

    const { data: employeesRaw = [] } = useQuery({
        queryKey: ["employees"],
        queryFn: async () => unwrap(await getEmployees()),
    });

    const { data: companyBenefitsRaw = [] } = useQuery({
        queryKey: ["company-benefits"],
        queryFn: getCompanyBenefits,
    });

    const { data: benefitData } = useQuery({
        queryKey: ['employee-benefit', benefitId],
        queryFn: () => getEmployeeBenefit(benefitId),
        enabled: isEditMode,
    });

    const companyBenefitList = Array.isArray(companyBenefitsRaw) ? companyBenefitsRaw : (companyBenefitsRaw?.data ?? []);

    useEffect(() => {
        if (isEditMode && benefitData && companyBenefitList.length > 0) {
            const b = benefitData;
            const linked = companyBenefitList.find(cb => cb.id === b.companyBenefitId);

            // Use enrollment amounts when > 0; fall back to linked plan defaults so the
            // employer sees the plan rates pre-filled instead of $0 on the Review page.
            const empAmount = b.employeeDeduction > 0 ? String(b.employeeDeduction)
                : b.employeeContributionAmount > 0 ? String(b.employeeContributionAmount)
                : linked?.employeeContributionAmount > 0 ? String(linked.employeeContributionAmount) : '';
            const empPercent = b.employeeContributionPercent > 0 ? String(b.employeeContributionPercent)
                : linked?.employeeContributionPercent > 0 ? String(linked.employeeContributionPercent) : '';
            const coAmount = b.companyContribution > 0 ? String(b.companyContribution)
                : b.companyContributionAmount > 0 ? String(b.companyContributionAmount)
                : linked?.companyContributionAmount > 0 ? String(linked.companyContributionAmount) : '';
            const coPercent = b.companyContributionPercent > 0 ? String(b.companyContributionPercent)
                : linked?.companyContributionPercent > 0 ? String(linked.companyContributionPercent) : '';

            setForm({
                employeeId: b.employeeId || '',
                companyBenefitId: b.companyBenefitId || '',
                type: b.type || linked?.type || '',
                effectiveStart: toDateInput(b.effectiveStart || linked?.effective),
                effectiveEnd: toDateInput(b.effectiveEnd || linked?.effectiveEnd),
                period: b.period || linked?.period || '',
                employeeContributionAmount: empAmount,
                employeeContributionPercent: empPercent,
                companyContributionAmount: coAmount,
                companyContributionPercent: coPercent,
                hsaContributionLimit: b.hsaContributionLimit || '',
            });
            if (empPercent) setEmployeeMode('percent');
            else setEmployeeMode('amount');
            if (coPercent) setCompanyMode('percent');
            else setCompanyMode('amount');
        }
    }, [isEditMode, benefitData, companyBenefitList]);

    const employeeList = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);
    const employeeOptions = employeeList.map(e => ({
        value: e.id,
        label: [e.firstName, e.lastName].filter(Boolean).join(" ") || e.id,
    }));

    const companyBenefitOptions = [
        { value: "", label: "No template — enter details manually" },
        ...companyBenefitList.map(b => ({
            value: b.id,
            label: [b.type, b.description].filter(Boolean).join(" — ") || b.id,
        })),
    ];

    const selectedTemplate = companyBenefitList.find(cb => cb.id === form.companyBenefitId);

    const { mutate: mutateCreate, isPending: isCreating, isError: isCreateError, error: createError } = useMutation({
        mutationFn: createEmployeeBenefit,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employee-benefits"] });
            const planLabel = selectedTemplate ? (selectedTemplate.type || 'Benefit') : (form.type || 'Benefit');
            notifyEmployee(form.employeeId, 'Benefit Enrollment Confirmed', `You have been enrolled in the ${planLabel} plan. Check your benefits page for details.`);
            navigate(returnTo);
        },
    });

    const { mutate: mutateUpdate, isPending: isUpdating, isError: isUpdateError, error: updateError } = useMutation({
        mutationFn: updateEmployeeBenefit,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employee-benefits"] });
            queryClient.invalidateQueries({ queryKey: ["employee-benefit"] });
            const planLabel = selectedTemplate ? (selectedTemplate.type || 'Benefit') : (form.type || 'Benefit');
            notifyEmployee(form.employeeId, 'Benefit Enrollment Updated', `Your ${planLabel} benefit enrollment has been updated. Review your benefits page for the latest details.`);
            navigate(returnTo);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'companyBenefitId') {
            const template = companyBenefitList.find(cb => cb.id === value);
            if (template) {
                setForm(prev => ({
                    ...prev,
                    companyBenefitId: value,
                    type: template.type || prev.type,
                    period: template.period || prev.period,
                    effectiveStart: toDateInput(template.effective || template.effectiveStart) || prev.effectiveStart,
                    effectiveEnd: toDateInput(template.effectiveEnd) || prev.effectiveEnd,
                    employeeContributionAmount: template.employeeContributionAmount > 0 ? String(template.employeeContributionAmount) : prev.employeeContributionAmount,
                    employeeContributionPercent: template.employeeContributionPercent > 0 ? String(template.employeeContributionPercent) : prev.employeeContributionPercent,
                }));
                if (template.employeeContributionPercent > 0) setEmployeeMode('percent');
                else if (template.employeeContributionAmount > 0) setEmployeeMode('amount');
            } else {
                setForm(prev => ({ ...prev, companyBenefitId: value }));
            }
            return;
        }

        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleEmployeeModeToggle = (mode) => {
        setEmployeeMode(mode);
        setForm(prev => ({ ...prev, employeeContributionAmount: '', employeeContributionPercent: '' }));
    };

    const handleCompanyModeToggle = (mode) => {
        setCompanyMode(mode);
        setForm(prev => ({ ...prev, companyContributionAmount: '', companyContributionPercent: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.employeeId) newErrors.employeeId = "Employee is required";
        if (!form.type && !form.companyBenefitId) newErrors.type = "Please select a benefit type or choose a benefit plan template";
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        const payload = { employeeId: form.employeeId };
        if (form.companyBenefitId) payload.companyBenefitId = form.companyBenefitId;
        if (form.type) payload.type = form.type;
        if (form.effectiveStart) payload.effectiveStart = new Date(form.effectiveStart).toISOString();
        if (form.effectiveEnd) payload.effectiveEnd = new Date(form.effectiveEnd).toISOString();
        if (form.period) payload.period = form.period;
        // Employee deduction — always clear the unused mode so old values don't persist
        if (employeeMode === 'amount') {
            payload.employeeContributionAmount = form.employeeContributionAmount ? parseFloat(form.employeeContributionAmount) : null;
            payload.employeeDeduction          = form.employeeContributionAmount ? parseFloat(form.employeeContributionAmount) : null;
            payload.employeeContributionPercent = null;
        } else {
            payload.employeeContributionPercent = form.employeeContributionPercent ? parseFloat(form.employeeContributionPercent) : null;
            payload.employeeContributionAmount  = null;
            payload.employeeDeduction           = null;
        }
        // Employer contribution — use 'companyContribution' (API field name) and clear unused mode
        if (companyMode === 'amount') {
            payload.companyContribution        = form.companyContributionAmount ? parseFloat(form.companyContributionAmount) : null;
            payload.companyContributionAmount  = form.companyContributionAmount ? parseFloat(form.companyContributionAmount) : null;
            payload.companyContributionPercent = null;
        } else {
            payload.companyContributionPercent = form.companyContributionPercent ? parseFloat(form.companyContributionPercent) : null;
            payload.companyContribution        = null;
            payload.companyContributionAmount  = null;
        }
        if (form.hsaContributionLimit) payload.hsaContributionLimit = form.hsaContributionLimit;
        if (isEditMode) payload.pendingReview = false;
        if (isEditMode) mutateUpdate({ benefitId, ...payload });
        else mutateCreate(payload);
    };

    const isPending = isCreating || isUpdating;
    const isError = isCreateError || isUpdateError;
    const error = createError || updateError;

    const isPendingReview = isEditMode && benefitData?.pendingReview === true;

    return (
        <form className={cn("container-spacing pt-3 pb-5")} onSubmit={handleSubmit}>
            <h3 className={cn("title mt-2")}>{isEditMode ? 'Edit Enrollment' : 'Enroll Employee in Benefit'}</h3>
            {isPendingReview && (
                <div style={{ background: '#fff8e1', border: '1px solid #f59e0b', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    <div>
                        <strong style={{ color: '#92400e', fontSize: 14 }}>Action Required — Self-Onboarded Benefit</strong>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78350f' }}>
                            This benefit was enrolled by the employee during self-onboarding. Contribution amounts have been pre-filled from the plan defaults. <strong>Review and adjust if needed</strong>, then click "Update Enrollment" to approve.
                        </p>
                    </div>
                </div>
            )}
            <div>
                <p className={cn("info-title mt-3")}>Employee & Plan</p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-lg-8 col-12">
                            <Select label="Employee*" fullWidth placeholder="Select employee" options={employeeOptions} name="employeeId" value={form.employeeId} onChange={handleChange} error={!!errors.employeeId} errorText={errors.employeeId} />
                        </div>
                        <div className="col-lg-8 col-12">
                            <Select
                                label="Benefit Plan (Optional)"
                                fullWidth
                                placeholder="Select a plan to auto-fill details"
                                options={companyBenefitOptions}
                                name="companyBenefitId"
                                value={form.companyBenefitId}
                                onChange={handleChange}
                            />
                            {selectedTemplate && (
                                <div style={{ marginTop: 6, padding: '6px 12px', background: '#e8f5e9', borderRadius: 8, fontSize: 12, color: '#2e7d32' }}>
                                    ✓ Using <strong>{selectedTemplate.type} — {selectedTemplate.description}</strong>. Fields below are pre-filled from this plan.
                                </div>
                            )}
                        </div>
                        <div className="col-lg-8 col-12">
                            <Select
                                label={`Benefit Type${!form.companyBenefitId ? '*' : ''}`}
                                fullWidth
                                placeholder="Select benefit type"
                                options={BENEFIT_TYPES}
                                name="type"
                                value={form.type}
                                onChange={handleChange}
                                error={!!errors.type}
                                errorText={errors.type}
                            />
                        </div>
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Enrollment Details</p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-lg-4 col-12">
                            <Input label="Start Date" fullWidth variant="flat" type="date" name="effectiveStart" value={form.effectiveStart} onChange={handleChange} />
                        </div>
                        <div className="col-lg-4 col-12">
                            <Input label="End Date" fullWidth variant="flat" type="date" name="effectiveEnd" value={form.effectiveEnd} onChange={handleChange} />
                        </div>
                        <div className="col-lg-4 col-12">
                            <Select label="Contribution Period" fullWidth placeholder="Select period" options={BENEFIT_PERIODS} name="period" value={form.period} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Employee Deduction</p>
                <p style={{ fontSize: 13, color: '#888', marginTop: -8, marginBottom: 12 }}>
                    How much is deducted from this employee's paycheck each period.
                    {selectedTemplate && <span> (Pre-filled from plan — adjust if this employee has a different rate.)</span>}
                </p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-lg-4 col-12">
                            <ContribToggle mode={employeeMode} onToggle={handleEmployeeModeToggle} />
                            {employeeMode === 'amount'
                                ? <Input label="Deduction Amount ($)" fullWidth variant="flat" type="number" min="0" placeholder="0.00" name="employeeContributionAmount" value={form.employeeContributionAmount} onChange={handleChange} />
                                : <Input label="Deduction Percent (%)" fullWidth variant="flat" type="number" min="0" max="100" placeholder="0.00" name="employeeContributionPercent" value={form.employeeContributionPercent} onChange={handleChange} />
                            }
                        </div>
                        {form.type === 'HSA' && (
                            <div className="col-lg-4 col-12">
                                <Input
                                    label="HSA Annual Contribution Limit"
                                    fullWidth
                                    variant="flat"
                                    name="hsaContributionLimit"
                                    value={form.hsaContributionLimit}
                                    onChange={handleChange}
                                    placeholder="e.g. 3850"
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>IRS limit for this employee's HSA contributions this year</div>
                            </div>
                        )}
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Employer Contribution</p>
                <p style={{ fontSize: 13, color: '#888', marginTop: -8, marginBottom: 12 }}>
                    How much the company contributes toward this benefit each period. Leave blank if the company does not contribute.
                </p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-lg-4 col-12">
                            <ContribToggle mode={companyMode} onToggle={handleCompanyModeToggle} />
                            {companyMode === 'amount'
                                ? <Input label="Company Contribution ($)" fullWidth variant="flat" type="number" min="0" placeholder="0.00" name="companyContributionAmount" value={form.companyContributionAmount} onChange={handleChange} />
                                : <Input label="Company Contribution (%)" fullWidth variant="flat" type="number" min="0" max="100" placeholder="0.00" name="companyContributionPercent" value={form.companyContributionPercent} onChange={handleChange} />
                            }
                        </div>
                    </div>
                </div>

                <div className="mt-5">
                    {isError && (
                        <p className="text-danger mb-2">{error?.response?.data?.message || error?.response?.data?.title || "Failed to save enrollment. Please try again."}</p>
                    )}
                    <Button type="submit" disabled={isPending} className="me-3">{isPending ? "Saving..." : (isEditMode ? "Update Enrollment" : "Enroll Employee")}</Button>
                    <Button variant="secondary" type="button" onClick={() => navigate(returnTo)}>Cancel</Button>
                </div>
            </div>
        </form>
    );
};

export default AddEditEmployeeBenefit;
